import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormHelperText,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { formatNumberWithComma } from "@utils/index";

export type IStepperInput = Omit<
  TextFieldProps,
  "minValue" | "maxValue" | "maxLength" | "onChange" | "textFieldWidth" | "commaNum"
> & {
  minValue?: number;
  maxValue?: number;
  maxLength?: number;
  onChange?: (value: string) => void;
  textFieldWidth?: number;
  commaNum?: string;
};

const StepperInput: React.FC<IStepperInput> = ({
  minValue = 0,
  maxValue = 99,
  maxLength,
  textFieldWidth = 100,
  value,
  onChange,
  sx,
  inputProps,
  helperText,
  defaultValue,
  commaNum = "",
  ...props
}) => {
  const [text, setText] = useState("0");
  const [textComma, setTextComma] = useState("0");
  const [focus, setFocus] = useState(false);
  const isNumber = useMemo(() => !isNaN(Number(text)), [text]);

  useEffect(() => {
    if (focus) return;
    let new_text = (value ?? defaultValue ?? 0).toString();
    let new_text_comma = new_text;
    if (commaNum !== "") {
      new_text_comma = formatNumberWithComma(new_text, commaNum);
    }
    setText(new_text);
    setTextComma(new_text_comma);
  }, [value, defaultValue, commaNum, focus]);

  const handleSubtract = () => {
    let new_value = (Number(text) - 1).toString();
    if (maxLength!==undefined && new_value.length > maxLength) {
      new_value = new_value.substring(0, maxLength);
    }
    setText(new_value);
    if (onChange) onChange(new_value);
  };

  const handleAdd = () => {
    let new_value = (Number(text) + 1).toString();
    if (maxLength!==undefined && new_value.length > maxLength) {
      new_value = new_value.substring(0, maxLength);
    }
    setText(new_value);
    if (onChange) onChange(new_value);
  };

  const sx_width = sx ? JSON.parse(JSON.stringify(sx)).width : undefined;

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{
          gap: "3px",
          minWidth: (sx_width ?? textFieldWidth) + 30 * 2 + 3 * 2,
        }}
      >
        <Button
          sx={styles.button}
          onClick={() => handleSubtract()}
          disabled={
            Number(text) <= minValue ||
            props.error ||
            !isNumber ||
            props.disabled
          }
        >
          -
        </Button>
        <TextField
          value={focus ? text : textComma}
          onChange={(e) => {
            setText(e.target.value);
            if (onChange) onChange(e.target.value);
          }}
          sx={{ width: textFieldWidth, ...sx }}
          inputProps={{
            style: { textAlign: "center" },
            maxLength: maxValue.toString().length,
            ...inputProps,
          }}
          {...props}
          onFocus={(e) => {
            setFocus(true);
            setText(text.replace(",", ""));
            if (props.onFocus) props.onFocus(e);
          }}
          onBlur={(e) => {
            setFocus(false);
            if (props.onBlur) props.onBlur(e);
          }}
        />
        <Button
          sx={styles.button}
          onClick={() => handleAdd()}
          disabled={
            Number(text) >= maxValue ||
            props.error ||
            !isNumber ||
            props.disabled
          }
        >
          +
        </Button>
      </Box>
      {helperText && (
        <FormHelperText error={props.error} sx={{ mx: 0 }}>
          {helperText}
        </FormHelperText>
      )}
    </>
  );
};

export default StepperInput;

const styles = {
  button: {
    height: 30,
    minWidth: 30,
    maxWidth: 30,
    borderRadius: 15,
    padding: 0,
  },
} as const;
